// ============================================================================
// INVENTORY CORE ENDPOINTS (ADR-0058 slice 1)
// Base paths mirror the controllers' [Route] attributes under api/v1/inventory.
// ============================================================================

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

const CATEGORY_BASE = '/inventory/item-categories';
export const itemCategoryEndpoints = {
  list: () => buildEndpoint(CATEGORY_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${CATEGORY_BASE}/:id`, { id }),
  create: () => buildEndpoint(CATEGORY_BASE),
  update: (id: ID) => buildEndpointWithParams(`${CATEGORY_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${CATEGORY_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${CATEGORY_BASE}/validate/code/:code`, { code }),
};

const CLASSIFICATION_BASE = '/inventory/classifications';
export const classificationEndpoints = {
  list: () => buildEndpoint(CLASSIFICATION_BASE),
  /** Active dimensions (no values) — the item screen's panel; item-master VIEW. */
  active: () => buildEndpoint(`${CLASSIFICATION_BASE}/active`),
  /** Paged values of one dimension — pickers; item-master VIEW. */
  values: () => buildEndpoint(`${CLASSIFICATION_BASE}/values`),
  getById: (id: ID) => buildEndpointWithParams(`${CLASSIFICATION_BASE}/:id`, { id }),
  create: () => buildEndpoint(CLASSIFICATION_BASE),
  update: (id: ID) => buildEndpointWithParams(`${CLASSIFICATION_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${CLASSIFICATION_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${CLASSIFICATION_BASE}/validate/code/:code`, { code }),
};

const LOCATION_BASE = '/inventory/stock-locations';
export const stockLocationEndpoints = {
  list: () => buildEndpoint(LOCATION_BASE),
  /** Storage locations for the item screen's default-location picker; item-master VIEW. */
  lookup: () => buildEndpoint(`${LOCATION_BASE}/lookup`),
  getById: (id: ID) => buildEndpointWithParams(`${LOCATION_BASE}/:id`, { id }),
  create: () => buildEndpoint(LOCATION_BASE),
  update: (id: ID) => buildEndpointWithParams(`${LOCATION_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${LOCATION_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${LOCATION_BASE}/validate/code/:code`, { code }),
};

const SETTINGS_BASE = '/inventory/settings';
export const inventorySettingsEndpoints = {
  get: () => buildEndpoint(SETTINGS_BASE),
  savePolicy: () => buildEndpoint(`${SETTINGS_BASE}/policy`),
  setLedgerDefault: () => buildEndpoint(`${SETTINGS_BASE}/ledger-defaults`),
  clearLedgerDefault: (purposeCode: string) => buildEndpointWithParams(`${SETTINGS_BASE}/ledger-defaults/:purposeCode`, { purposeCode }),
  overrides: () => buildEndpoint(`${SETTINGS_BASE}/ledger-overrides`),
  deleteOverride: (id: ID) => buildEndpointWithParams(`${SETTINGS_BASE}/ledger-overrides/:id`, { id }),
};

const ITEM_BASE = '/inventory/items';
export const itemEndpoints = {
  list: () => buildEndpoint(ITEM_BASE),
  /** Domain modules whose item tabs are enabled. */
  extensions: () => buildEndpoint(`${ITEM_BASE}/extensions`),
  getById: (id: ID) => buildEndpointWithParams(`${ITEM_BASE}/:id`, { id }),
  create: () => buildEndpoint(ITEM_BASE),
  update: (id: ID) => buildEndpointWithParams(`${ITEM_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${ITEM_BASE}/:id`, { id }),
  validateCode: (code: string) => buildEndpointWithParams(`${ITEM_BASE}/validate/code/:code`, { code }),
};

const REPORT_BASE = '/inventory/reports';
export const stockReportEndpoints = {
  stockPosition: () => buildEndpoint(`${REPORT_BASE}/stock-position`),
  stockCard: () => buildEndpoint(`${REPORT_BASE}/stock-card`),
};

const OPENING_STOCK_BASE = '/stockops/opening-stock';
export const openingStockEndpoints = {
  list: () => buildEndpoint(OPENING_STOCK_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${OPENING_STOCK_BASE}/:id`, { id }),
  create: () => buildEndpoint(OPENING_STOCK_BASE),
  update: (id: ID) => buildEndpointWithParams(`${OPENING_STOCK_BASE}/:id`, { id }),
  cancel: (id: ID) => buildEndpointWithParams(`${OPENING_STOCK_BASE}/:id`, { id }),
};
